import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import get from 'lodash/get';
import noop from 'lodash/noop';

import { useAccessToken, useSnackBar } from '@sbv-clients/ui-shared';
import { UnwrapArray } from '@sbv-clients/util-misc';

import {
  Api,
  GetMasterViewRecordResponse,
  MasterScheduleApiMasterScheduleAssignEmployeeDraftPostRequest,
  MasterScheduleApiMasterScheduleMasterViewGetRequest,
  MasterScheduleApiMasterScheduleOverbudgetValidationIdGetRequest,
  MasterScheduleApiMasterSchedulePostRequest,
  MasterScheduleApiMasterSchedulePublishIdPatchRequest,
  MasterScheduleApiMasterScheduleRemoveEmployeeDraftDeleteRequest,
  useApi,
  ViewOpeningTemplateDto,
} from '../index';
import {
  OpeningTemplateCacheKeys,
  SchedulerMasterViewCacheKeys,
} from './util/cache-keys';

export type OpeningTemplateType = ViewOpeningTemplateDto;
type MasterViewResponse = GetMasterViewRecordResponse;
export type MasterViewPosition = NonNullable<
  UnwrapArray<MasterViewResponse['positions']>
>;
export type MasterViewShiftType = NonNullable<
  UnwrapArray<MasterViewPosition['shiftTypes']>
>;
export type MasterViewUnit = NonNullable<
  UnwrapArray<MasterViewShiftType['units']>
>;

const getMasterScheduleDraftsCacheKey = () => [
  SchedulerMasterViewCacheKeys.Drafts,
];

export const useGetMasterScheduleDrafts = () => {
  const api = useApi();
  return useQuery({
    queryKey: getMasterScheduleDraftsCacheKey(),
    queryFn: async () => {
      return api.masterScheduleApi
        .masterScheduleDraftsGet()
        .then((response) => response.data ?? []);
    },
    retry: false,
    refetchOnWindowFocus: false,
  });
};

export const useShouldRemoveDraft = () => {
  const api = useApi();
  return useMutation(async (draftId: number) =>
    api.masterScheduleApi
      .masterScheduleDraftIdGet({ id: draftId })
      .then((response) => {
        return !response.data.masterScheduleDraftElements?.length;
      })
  );
};

export type MasterScheduleDraftType = NonNullable<
  UnwrapArray<ReturnType<typeof useGetMasterScheduleDrafts>['data']>
>;

export const masterViewCacheKey = (
  props: MasterScheduleApiMasterScheduleMasterViewGetRequest
) => {
  return [
    SchedulerMasterViewCacheKeys.MasterView,
    props.facilityId as number,
    props.departmentId as number,
    props.weekNumber as number,
    props.positionId as number,
    props.shiftTypeId as number,
    props.masterScheduleDraftId as number,
  ];
};

export const masterViewGet =
  (api: Api, props: MasterScheduleApiMasterScheduleMasterViewGetRequest) =>
  async () => {
    const always = {
      weekNumber: props.weekNumber,
      departmentId: props.departmentId,
      facilityId: props.facilityId,
    };
    const params: MasterScheduleApiMasterScheduleMasterViewGetRequest =
      props.masterScheduleDraftId
        ? {
            ...always,
            masterScheduleDraftId: props.masterScheduleDraftId,
          }
        : {
            ...always,
            ...(props.shiftTypeId != null && {
              shiftTypeId: props.shiftTypeId,
            }),
            ...(props.positionId != null && {
              positionId: props.positionId,
            }),
          };
    const response = await api.masterScheduleApi.masterScheduleMasterViewGet(
      params
    );
    return response?.data.data ?? {};
  };

export const useMasterViewGet = (
  props: MasterScheduleApiMasterScheduleMasterViewGetRequest,
  enabled: boolean
) => {
  const api = useApi();
  return useQuery({
    queryKey: masterViewCacheKey(props),
    queryFn: masterViewGet(api, props),
    retry: false,
    refetchOnWindowFocus: false,
    enabled,
  });
};

export type MasterViewResponseType = NonNullable<
  ReturnType<typeof useMasterViewGet>['data']
>;

/**
 * Returns a substring of the input string until the specified character is found.
 * @param input The input string.
 * @param character The character to stop at.
 * @returns The substring until the specified character.
 */
export function getStringUntilCharacter(
  input: string,
  character: string
): string {
  const index = input.indexOf(character);
  if (index === -1) {
    return input; // Character not found, return the whole string
  }
  return input.substring(0, index);
}

const beginDraft =
  (api: Api, setIsDraftsLoading: (value: boolean) => void) =>
  (requestParameters: MasterScheduleApiMasterSchedulePostRequest) => {
    setIsDraftsLoading(true);
    return api.masterScheduleApi.masterSchedulePost(requestParameters);
  };

export const useBeginDraft = (
  setDraftId: (value: number) => void,
  setIsLoading: (value: boolean) => void
) => {
  const api = useApi();
  const queryClient = useQueryClient();
  const { notifyError, notifySuccess } = useSnackBar();
  return useMutation(beginDraft(api, setIsLoading), {
    onSuccess: async (data, variables, context) => {
      notifySuccess(
        `Successfully created a draft and ${
          variables.createMasterScheduleDraftDto
            ?.masterScheduleDraftElements?.[0].action === 'Added'
            ? 'added'
            : 'removed'
        } an assignment.`
      );
      const masterScheduleDraftId = data.data.data?.masterScheduleDraftId;
      if (masterScheduleDraftId) {
        setDraftId(masterScheduleDraftId);
      } else {
        notifyError('Draft id not generated');
      }

      queryClient
        .invalidateQueries([SchedulerMasterViewCacheKeys.Drafts])
        .catch(noop);
      queryClient
        .invalidateQueries([OpeningTemplateCacheKeys.AvailableEmployees])
        .catch(noop);
      queryClient
        .invalidateQueries([SchedulerMasterViewCacheKeys.MasterView])
        .catch(noop);
      setIsLoading(false);
    },
    onError: async (error) => {
      const message = getStringUntilCharacter(
        get(error, 'response.data.message[0]', ''),
        '\n'
      );
      notifyError(message);
    },
  });
};

const addInDraft =
  (api: Api) =>
  (
    requestParameters: MasterScheduleApiMasterScheduleAssignEmployeeDraftPostRequest
  ) => {
    return api.masterScheduleApi.masterScheduleAssignEmployeeDraftPost(
      requestParameters
    );
  };

export const useAddInDraft = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  const { notifyError, notifySuccess } = useSnackBar();
  return useMutation(addInDraft(api), {
    onSuccess: async () => {
      notifySuccess('Assignment successfully added to draft');
      queryClient
        .invalidateQueries([SchedulerMasterViewCacheKeys.MasterView])
        .catch(noop);
      queryClient
        .invalidateQueries([OpeningTemplateCacheKeys.AvailableEmployees])
        .catch(noop);
      queryClient
        .invalidateQueries([SchedulerMasterViewCacheKeys.Drafts])
        .catch(noop);
    },
    onError: async (error) => {
      const message = getStringUntilCharacter(
        get(error, 'response.data.message[0]', ''),
        '\n'
      );
      notifyError(message);
    },
  });
};

const removeInDraft =
  (api: Api) =>
  (
    requestParameters: MasterScheduleApiMasterScheduleRemoveEmployeeDraftDeleteRequest
  ) => {
    return api.masterScheduleApi.masterScheduleRemoveEmployeeDraftDelete(
      requestParameters
    );
  };
export const useRemoveInDraft = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  const { notifyError, notifySuccess } = useSnackBar();
  return useMutation(removeInDraft(api), {
    onSuccess: async () => {
      notifySuccess('Assignment successfully removed from draft');
      await queryClient
        .invalidateQueries([SchedulerMasterViewCacheKeys.MasterView])
        .catch(noop);
      await queryClient
        .invalidateQueries([OpeningTemplateCacheKeys.AvailableEmployees])
        .catch(noop);
      await queryClient
        .invalidateQueries([SchedulerMasterViewCacheKeys.Drafts])
        .catch(noop);
    },
    onError: async (error) => {
      const message = getStringUntilCharacter(
        get(error, 'response.data.message[0]', ''),
        '\n'
      );
      notifyError(message);
    },
  });
};

const deleteDraftElement = (api: Api) => (draftElementId: number) => {
  return api.masterScheduleApi.masterScheduleElementIdDelete({
    id: draftElementId,
  });
};

export const useDeleteDraftElement = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  const { notifyError, notifySuccess } = useSnackBar();
  return useMutation(deleteDraftElement(api), {
    onSuccess: async (data, variables, context) => {
      await queryClient
        .invalidateQueries([SchedulerMasterViewCacheKeys.MasterView])
        .catch(noop);
      await queryClient
        .invalidateQueries([SchedulerMasterViewCacheKeys.Drafts])
        .catch(noop);
      await queryClient
        .invalidateQueries([OpeningTemplateCacheKeys.AvailableEmployees])
        .catch(noop);
      notifySuccess('Draft element removed successfully');
    },
    onError: async (error, variables, context) => {
      console.error(error);
      const message = getStringUntilCharacter(
        get(error, 'response.data.message[0]', ''),
        '\n'
      );

      notifyError(`There was a problem removing draft element\n${message}`);
    },
  });
};

const discardDraft = (api: Api) => (draftId: number) => {
  return api.masterScheduleApi.masterScheduleIdDelete({ id: draftId });
};

export const useDiscardDraft = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  const { notifyError, notifySuccess } = useSnackBar();
  return useMutation(discardDraft(api), {
    onSuccess: async (data, variables) => {
      queryClient
        .invalidateQueries([SchedulerMasterViewCacheKeys.Drafts])
        .catch(noop);
      notifySuccess(`Draft #${variables} successfully removed`);
    },
    onError: async (error, variables, context) => {
      console.error(error);
      const message = getStringUntilCharacter(get(error, 'message', ''), '\n');
      notifyError(
        `There was a problem deleting draft #${variables}\n${message}`
      );
    },
  });
};

const renameDraft =
  (api: Api) =>
  ({ draftName, draftId }: { draftId: number; draftName: string }) => {
    return api.masterScheduleApi.masterScheduleIdNamePatch({
      id: draftId,
      name: draftName,
    });
  };

export const useRenameDraft = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  const { notifyError, notifySuccess } = useSnackBar();
  return useMutation(renameDraft(api), {
    onSuccess: async (data, variables, context) => {
      queryClient
        .invalidateQueries([SchedulerMasterViewCacheKeys.Drafts])
        .catch(noop);
      notifySuccess(`Draft #${variables.draftId} successfully renamed`);
    },
    onError: async (error, variables, context) => {
      notifyError(`There was a problem renaming draft #${variables.draftId}`);
    },
  });
};

export const usePublishDraft = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  const { notifySuccess } = useSnackBar();
  return useMutation(
    (body: MasterScheduleApiMasterSchedulePublishIdPatchRequest) =>
      api.masterScheduleApi.masterSchedulePublishIdPatch(body),
    {
      onSuccess: async (data, variables) => {
        queryClient
          .invalidateQueries([SchedulerMasterViewCacheKeys.Drafts])
          .catch(noop);
        notifySuccess(`Draft #${variables.id} successfully published`);
      },
    }
  );
};

export const useValidateOverBudget = (draftId: number) => {
  const api = useApi();
  return useQuery({
    queryKey: [SchedulerMasterViewCacheKeys.Overbudget, draftId],
    queryFn: () =>
      api.masterScheduleApi
        .masterScheduleOverbudgetValidationIdGet({
          id: draftId,
        })
        .then((response) => response.data),
    retry: false,
    enabled: false,
    refetchOnWindowFocus: false,
  });
};

export const useValidateOverBudgetWithMutation = () => {
  const api = useApi();
  return useMutation({
    mutationFn: (
      body: MasterScheduleApiMasterScheduleOverbudgetValidationIdGetRequest
    ) =>
      api.masterScheduleApi
        .masterScheduleOverbudgetValidationIdGet(body)
        .then((response) => response.data),
    retry: false,
  });
};
